import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { syncApi, budgetApi, ApiException } from '@/lib/api';
import { flattenBudgetsForStorage } from '@/lib/budgetTree';
import { TransactionService } from '@/services/TransactionService';
import {
  budgetDb,
  transactionDb,
  syncQueueDb,
  metadataDb,
  conflictDb,
  wipeLocalVaultAndData,
  QueuedChange
} from '@/lib/db';
import { setOfflineQueueHandler } from '@/lib/offlineQueue';
import type { SyncStatus, Budget, Transaction, EntityChange } from '@/types';
import { budgetKeys } from '@/hooks/useBudgets';
import { transactionKeys } from '@/hooks/useTransactions';
import { useAuth } from './AuthContext';

interface SyncContextType {
  status: SyncStatus;
  pendingChanges: number;
  conflictCount: number;
  lastSyncTime: string | null;
  isOnline: boolean;
  sync: () => Promise<void>;
  triggerSync: () => void;
  queueChange: (change: Omit<QueuedChange, 'id' | 'attempts' | 'createdAt'>) => Promise<void>;
  forceRefresh: () => Promise<void>;
  getPendingChanges: () => Promise<QueuedChange[]>;
}

const SyncContext = createContext<SyncContextType | undefined>(undefined);

const SYNC_INTERVAL = 60000; // 1 minute

export function SyncProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, user, sessionState, lockSession, handleTokenExpiration } = useAuth();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<SyncStatus>('synced');
  const [pendingChanges, setPendingChanges] = useState(0);
  const [conflictCount, setConflictCount] = useState(0);
  const [lastSyncTime, setLastSyncTime] = useState<string | null>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  const invalidateBudgetAndTransactionCaches = useCallback(async (budgetId?: string) => {
    await queryClient.invalidateQueries({ queryKey: budgetKeys.all });
    await queryClient.invalidateQueries({ queryKey: budgetKeys.summaries() });
    await queryClient.invalidateQueries({ queryKey: budgetKeys.activeSummaries() });
    await queryClient.invalidateQueries({ queryKey: transactionKeys.all });
    if (budgetId) {
      await queryClient.invalidateQueries({ queryKey: budgetKeys.detail(budgetId) });
    }
  }, [queryClient]);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Trigger sync when coming back online
      sync();
    };
    
    const handleOffline = () => {
      setIsOnline(false);
      setStatus('offline');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Initialize sync state
  useEffect(() => {
    if (isAuthenticated) {
      initializeSync();
    }
  }, [isAuthenticated]);

  // Periodic sync
  useEffect(() => {
    if (!isAuthenticated || !isOnline) return;

    const interval = setInterval(() => {
      sync();
    }, SYNC_INTERVAL);

    return () => clearInterval(interval);
  }, [isAuthenticated, isOnline]);

  const initializeSync = async () => {
    try {
      const stored = await metadataDb.getLastSyncTime();
      setLastSyncTime(stored);

      const pending = await syncQueueDb.count();
      setPendingChanges(pending);

      const conflicts = await conflictDb.count();
      setConflictCount(conflicts);

      if (pending > 0) {
        setStatus('offline');
      } else if (conflicts > 0) {
        setStatus('conflict');
      }

      // Initial sync
      if (isOnline) {
        await sync();
      }
    } catch (error) {
      console.error('Failed to initialize sync:', error);
    }
  };

  const sync = useCallback(async () => {
    // localUnlocked / offline: do not push to server
    if (!isOnline || !isAuthenticated || sessionState === 'localUnlocked') {
      setStatus('offline');
      return;
    }

    try {
      setStatus('syncing');

      // Get pending changes from queue
      const queuedChanges = await syncQueueDb.getPending();
      const changes: EntityChange[] = queuedChanges.map(qc => ({
        entityType: qc.entityType,
        entityId: qc.entityId,
        changeType: qc.changeType,
        data: qc.data,
        version: qc.version,
        lastModified: qc.createdAt,
      }));

      const deviceId = await metadataDb.getDeviceId();
      const lastSync = await metadataDb.getLastSyncTime();

      // Send sync request
      const response = await syncApi.sync({
        lastSyncTime: lastSync,
        changes,
        deviceId,
      });

      // Process server changes
      for (const change of response.changes) {
        if (change.entityType === 'BUDGET') {
          const budget = JSON.parse(change.data) as Budget;
          if (change.changeType === 'DELETE') {
            await budgetDb.delete(change.entityId);
          } else {
            await budgetDb.saveMany(flattenBudgetsForStorage([budget]));
          }
        } else if (change.entityType === 'TRANSACTION') {
          const transaction = JSON.parse(change.data) as Transaction;
          if (change.changeType === 'DELETE') {
            await transactionDb.delete(change.entityId);
          } else {
            await transactionDb.save(transaction);
          }
        }
      }

      // Handle conflicts
      for (const conflict of response.conflicts) {
        await conflictDb.add(conflict);
      }

      // Handle shared budgets that need full refresh
      for (const budgetId of response.requiresRefresh) {
        try {
          const budget = await budgetApi.getById(budgetId);
          await budgetDb.saveMany(flattenBudgetsForStorage([budget]));

          const transactions = await TransactionService.getByBudgetTree(budgetId, true);
          await transactionDb.saveMany(transactions);
          await invalidateBudgetAndTransactionCaches(budgetId);
        } catch (error) {
          console.error(`Failed to refresh budget ${budgetId}:`, error);
        }
      }

      // Clear synced changes from queue
      for (const qc of queuedChanges) {
        if (qc.id) {
          await syncQueueDb.remove(qc.id);
        }
      }

      // Update sync metadata
      await metadataDb.setLastSyncTime(response.serverTime);
      setLastSyncTime(response.serverTime);

      // Update counts
      const remaining = await syncQueueDb.count();
      setPendingChanges(remaining);

      const conflicts = await conflictDb.count();
      setConflictCount(conflicts);

      // Set final status
      if (conflicts > 0) {
        setStatus('conflict');
      } else {
        setStatus('synced');
      }

    } catch (error) {
      console.error('Sync failed:', error);
      if (error instanceof ApiException && error.isUnauthorized) {
        const code = (error.error.code || '').toLowerCase();
        const message = error.error.message || '';
        const gone =
          code.includes('deleted') ||
          code.includes('account_gone') ||
          code.includes('user_not_found') ||
          message.toLowerCase().includes('no longer available');
        const revoked =
          code.includes('revoked') ||
          message.toLowerCase().includes('device was removed');

        if (gone || revoked) {
          // Do not sync queued changes for deleted/revoked accounts
          await wipeLocalVaultAndData();
          handleTokenExpiration(
            gone ? 'account_deleted' : 'device_revoked',
            gone
              ? 'This account is no longer available.'
              : 'This device was removed. Sign in with Google to continue.'
          );
          return;
        }

        lockSession(message || 'Your session expired. Unlock to continue.');
        return;
      }
      setStatus('offline');
    }
  }, [
    isOnline,
    isAuthenticated,
    sessionState,
    invalidateBudgetAndTransactionCaches,
    handleTokenExpiration,
    lockSession,
  ]);

  const queueChange = useCallback(async (
    change: Omit<QueuedChange, 'id' | 'attempts' | 'createdAt'>
  ) => {
    await syncQueueDb.add({
      ...change,
      attempts: 0,
      createdAt: new Date().toISOString(),
    });

    setPendingChanges(prev => prev + 1);

    // Try to sync immediately if online and fully authenticated
    if (isOnline && sessionState === 'onlineAuthenticated') {
      sync();
    } else {
      setStatus('offline');
    }
  }, [isOnline, sessionState, sync]);

  useEffect(() => {
    setOfflineQueueHandler(queueChange);
    return () => setOfflineQueueHandler(null);
  }, [queueChange]);

  const forceRefresh = useCallback(async () => {
    if (!isOnline || !isAuthenticated || !user || sessionState === 'localUnlocked') return;

    try {
      setStatus('syncing');

      // Clear local data
      await budgetDb.clear();
      await transactionDb.clear();

      // Fetch all data from server — flatten nested children so offline getById works
      const budgets = await budgetApi.getAll();
      await budgetDb.saveMany(flattenBudgetsForStorage(budgets));

      // Fetch transactions per root budget including descendants
      const rootBudgets = budgets.filter((b) => !b.parentId);
      for (const budget of rootBudgets) {
        const transactions = await TransactionService.getByBudgetTree(budget.id, true);
        await transactionDb.saveMany(transactions);
      }

      await invalidateBudgetAndTransactionCaches();

      // Update sync time
      const now = new Date().toISOString();
      await metadataDb.setLastSyncTime(now);
      setLastSyncTime(now);

      setStatus('synced');
    } catch (error) {
      console.error('Force refresh failed:', error);
      setStatus('offline');
    }
  }, [isOnline, isAuthenticated, user, sessionState, invalidateBudgetAndTransactionCaches]);

  const getPendingChanges = useCallback(async () => {
    return await syncQueueDb.getPending();
  }, []);

  const triggerSync = useCallback(() => {
    if (isOnline && isAuthenticated && sessionState === 'onlineAuthenticated') {
      sync();
    }
  }, [isOnline, isAuthenticated, sessionState, sync]);

  return (
    <SyncContext.Provider
      value={{
        status,
        pendingChanges,
        conflictCount,
        lastSyncTime,
        isOnline,
        sync,
        triggerSync,
        queueChange,
        forceRefresh,
        getPendingChanges,
      }}
    >
      {children}
    </SyncContext.Provider>
  );
}

export function useSync() {
  const context = useContext(SyncContext);
  if (context === undefined) {
    throw new Error('useSync must be used within a SyncProvider');
  }
  return context;
}
